// src/services/catalogPublicationService.js
//
// Frontend API wrapper for Catalog publication preview / history / publish flow.
//
// PURPOSE
// -------
// This service provides a stable frontend-facing API for the new Catalog publication layer.
//
// Main responsibilities:
// - create a persistent preview batch
// - list recent publication heads
// - load one publication head with items and summary
// - publish one already prepared head
//
// WHY THIS SERVICE EXISTS
// -----------------------
// Even though the backend publication flow is more complex internally,
// the frontend should talk to a small, explicit service surface.
//
// That gives us:
// - one place for all HTTP contracts
// - consistent error handling
// - minimal duplication in pages/components
// - easy future changes if backend contracts evolve
//
// IMPORTANT WORKFLOW NOTE
// -----------------------
// The preview endpoint does NOT return an ephemeral calculation only.
// It creates a real persisted publication head + items in the backend.
//
// This is important because later "Publish" must act on the same batch
// the user already reviewed.
//
// Logging policy (recommended)
// ----------------------------
// - Service layer should NOT spam console.log on success.
// - Service layer may log errors ONLY when a debug flag is enabled.
// - UI layer is responsible for user-facing messages and UX.
//
// Enable debug logs locally:
//   REACT_APP_DEBUG_API=true
//

import apiClient from "../utils/apiClient";

// Toggle error logging without changing code.
// In CRA / Vite, env values are strings.
const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

/**
 * Internal: extract a safe, readable message from Axios errors and backend bodies.
 */
function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

/**
 * Internal: build and throw a normalized Error that callers can handle.
 */
function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[CatalogPublicationService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Create a persistent preview batch for the current Catalog state.
 *
 * Backend:
 *   POST /catalog/publication/preview
 *
 * Body:
 *   {
 *     target_system: "wordpress",
 *     entity_scope: "catalog"
 *   }
 *
 * Return:
 *   {
 *     head: {...},
 *     items: [...],
 *     summary: {...}
 *   }
 *
 * IMPORTANT
 * ---------
 * This is not just a "calculate preview" helper.
 * It creates a real WebsitePublicationHead + WebsitePublicationItems batch.
 */
export const createPublicationPreview = async ({
  targetSystem = "wordpress",
  entityScope = "catalog",
} = {}) => {
  try {
    const res = await apiClient.post(
      "/catalog/publication/preview",
      {
        target_system: String(targetSystem || "wordpress"),
        entity_scope: String(entityScope || "catalog"),
      },
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("createPublicationPreview", err);
  }
};

/**
 * Fetch recent publication heads for history / inspection.
 *
 * Backend:
 *   GET /catalog/publication/heads
 *
 * Optional query params:
 *   - target_system
 *   - entity_scope
 *   - status
 *   - limit
 *
 * Return shape expected from backend:
 *   {
 *     items: [...],
 *     count: number
 *   }
 *
 * Return from this service:
 *   {
 *     items: [...],
 *     count: number
 *   }
 */
export const fetchPublicationHeads = async ({
  targetSystem = "wordpress",
  entityScope = "catalog",
  status = null,
  limit = 25,
} = {}) => {
  try {
    const params = {
      target_system: String(targetSystem || "wordpress"),
      entity_scope: String(entityScope || "catalog"),
      limit,
    };

    if (status) {
      params.status = String(status);
    }

    const res = await apiClient.get("/catalog/publication/heads", {
      withCredentials: true,
      params,
    });

    const payload = res?.data;
    const items = Array.isArray(payload?.items) ? payload.items : [];
    const count = Number.isFinite(Number(payload?.count))
      ? Number(payload.count)
      : items.length;

    return { items, count };
  } catch (err) {
    throwApiError("fetchPublicationHeads", err);
  }
};

/**
 * Fetch one publication head with all items and summary.
 *
 * Backend:
 *   GET /catalog/publication/heads/:id
 *
 * Return expected:
 *   {
 *     head: {...},
 *     items: [...],
 *     summary: {...}
 *   }
 */
export const fetchPublicationHead = async (headId) => {
  try {
    if (headId === undefined || headId === null || `${headId}`.trim() === "") {
      const e = new Error("fetchPublicationHead: headId is required.");
      e.code = "PUBLICATION_HEAD_ID_MISSING";
      throw e;
    }

    const res = await apiClient.get(`/catalog/publication/heads/${headId}`, {
      withCredentials: true,
    });

    const payload = res?.data;

    if (!payload?.head) {
      const e = new Error(
        "fetchPublicationHead: unexpected response shape (missing head)."
      );
      e.code = "PUBLICATION_HEAD_BAD_RESPONSE";
      e.data = payload;
      throw e;
    }

    return payload;
  } catch (err) {
    throwApiError("fetchPublicationHead", err);
  }
};

/**
 * Publish one already prepared publication head.
 *
 * Backend:
 *   POST /catalog/publication/heads/:id/publish
 *
 * IMPORTANT
 * ---------
 * This publishes the stored prepared batch.
 * It does NOT ask backend to rebuild preview at publish time.
 */
export const publishPublicationHead = async (headId) => {
  try {
    if (headId === undefined || headId === null || `${headId}`.trim() === "") {
      const e = new Error("publishPublicationHead: headId is required.");
      e.code = "PUBLICATION_HEAD_ID_MISSING";
      throw e;
    }

    const res = await apiClient.post(
      `/catalog/publication/heads/${headId}/publish`,
      {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
        timeout: 120000,
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("publishPublicationHead", err);
  }
};

export default {
  createPublicationPreview,
  fetchPublicationHeads,
  fetchPublicationHead,
  publishPublicationHead,
};